/**
 * Delivery Popup Module
 * 
 * This file contains all delivery popup related functionality:
 * - AddToCart class (handles delivery popup cart operations)
 * - DeliveryPopup class (main popup logic)
 * - GiftboxCalculator class (price calculations)
 * 
 * Dependencies (must be loaded before this file):
 * - Delegate class from global.js
 * - window.Shopify (Shopify global object)
 * - window.theme (theme settings)
 * - window.apiService (API service for requests)
 * 
 * Usage:
 * Load this file AFTER global.js on product and cart pages:
 * {{ 'delivery-popup.js' | asset_url | script_tag }}
 */


class DeliveryForm extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegateElement = new Delegate(this.element); 

    // Initialize components
    this.initCharacterCounting();
    this.initFormValidation();
    this.initEnterKeyNavigation();
    if (this.element.dataset.sync) {
      this.initShippingSync();
    }

  }
  initShippingSync() {
    console.log('DeliveryForm: Initializing shipping sync...');

    // IMPROVED: Better popup detection with retry logic
    this.establishPopupConnection();

    // Get all form inputs that should sync to shipping state
    const shippingInputs = this.element.querySelectorAll(`
    #recipient_name,
    #recipient_email, 
    #recipient_address,
    #recipient_city,
    #recipient_zip,
    #greeting_message,
    #schedule_delivery,
    #delivery_datetime
  `);

    if (shippingInputs.length === 0) {
      console.warn('DeliveryForm: No shipping inputs found to sync');
      return;
    }

    console.log(`DeliveryForm: Found ${shippingInputs.length} shipping inputs to sync`);

    // Debounced update function
    this.debouncedShippingUpdate = debounce((fieldName, value) => {
      if (this.popup && this.popup.updateShippingData) {
        this.popup.updateShippingData(fieldName, value);
      } else {
        console.warn('DeliveryForm: Cannot update shipping data, popup not connected');
      }
    }, 300);

    // Add event listeners to each input
    shippingInputs.forEach(input => {
      const fieldName = this.getFieldNameFromId(input.id);

      if (input.type === 'checkbox') {
        input.addEventListener('change', () => {
          this.debouncedShippingUpdate(fieldName, input.checked);
        });
      } else {
        // For text, email, textarea, datetime-local
        input.addEventListener('input', () => {
          this.debouncedShippingUpdate(fieldName, input.value.trim());
        });
      }

      // GTM: custom_shipping_form_started (fires once per popup open via flag)
      input.addEventListener('focusin', () => {
        const popup = this.element.closest('delivery-popup');
        if (window.dataLayer && popup && !popup._gtmShippingFormStarted) {
          popup._gtmShippingFormStarted = true;
          dataLayer.push({
            event: 'custom_shipping_form_started',
            delivery_type: popup.state ? popup.state.choices.delivery_type || '' : '',
            delivery_person: popup.state ? popup.state.choices.delivery_person || '' : ''
          });
        }
      });
    });

    // Form data will be populated by DeliveryPopup.loadAndPopulateFromLocalStorage()
  }

  // NEW: Better popup connection establishment
  establishPopupConnection(attempt = 1) {
    this.popup = this.element.closest('delivery-popup');

    if (this.popup) {
      console.log(`DeliveryForm: Popup connection established on attempt ${attempt}`);
      return true;
    }

    console.warn(`DeliveryForm: Popup not found on attempt ${attempt}`);

    if (attempt < 5) {
      setTimeout(() => {
        this.establishPopupConnection(attempt + 1);
      }, 100);
    } else {
      console.error('DeliveryForm: Failed to establish popup connection after 5 attempts');
    }

    return false;
  }

  // REMOVED: forceRestoreShippingData() - no longer needed
  // Data is populated by DeliveryPopup.loadAndPopulateFromLocalStorage()

  //  NEW: Map input IDs to shipping state field names
  getFieldNameFromId(inputId) {
    const fieldMap = {
      'recipient_name': 'recipient_name',
      'recipient_email': 'recipient_email',
      'recipient_address': 'recipient_address',
      'recipient_city': 'recipient_city',
      'recipient_zip': 'recipient_zip',
      'greeting_message': 'greeting_message',
      'schedule_delivery': 'schedule_delivery',
      'delivery_datetime': 'delivery_datetime'
    };

    return fieldMap[inputId] || inputId;
  }

  //  NEW: Debounce utility function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // REMOVED: restoreShippingFormData() - no longer needed
  // Data is populated by DeliveryPopup.loadAndPopulateFromLocalStorage()

  initCharacterCounting() {
    const textarea = this.element.querySelector('#greeting_message');
    const charCountDisplay = this.element.querySelector('.delivery__char-count');

    if (!textarea || !charCountDisplay) return;

    const maxLength = parseInt(textarea.getAttribute('maxlength')) || 65;

    // Update character count display
    const updateCharCount = () => {
      const currentLength = textarea.value.length;
      const remaining = maxLength - currentLength;

      // Update the display text
      const charsLeftLabel = window.translations?.products?.delivery_greeting_form?.chars_left || 'characters left';
      charCountDisplay.textContent = `${remaining} ${charsLeftLabel}`;
    };

    // Handle paste events separately
    const handlePaste = (e) => {
      e.preventDefault();

      const pasteData = (e.clipboardData || window.clipboardData).getData('text');
      const currentText = textarea.value;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // Calculate how much text we can insert
      const textBeforeSelection = currentText.substring(0, start);
      const textAfterSelection = currentText.substring(end);
      const availableSpace = maxLength - textBeforeSelection.length - textAfterSelection.length;

      if (availableSpace > 0) {
        const textToInsert = pasteData.substring(0, availableSpace);
        textarea.value = textBeforeSelection + textToInsert + textAfterSelection;
        textarea.setSelectionRange(start + textToInsert.length, start + textToInsert.length);
      }

      updateCharCount();
    };

    // Prevent input beyond maxlength for keyboard input
    const handleKeydown = (e) => {
      if (textarea.value.length >= maxLength) {
        // Allow backspace, delete, arrow keys, etc.
        const allowedKeys = [
          'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
          'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'
        ];

        if (!allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          return false;
        }
      }
    };

    // Event listeners
    textarea.addEventListener('input', updateCharCount);
    textarea.addEventListener('keydown', handleKeydown);
    textarea.addEventListener('paste', handlePaste);

    // Initial count update
    updateCharCount();
  }

  initFormValidation() {
    const form = this.element.querySelector('form');
    if (!form) return;

    // Initialize the form validator
    this.validator = new FormValidator(form, {
      errorClass: 'field--error',
      fieldSelector: '.field',
      buttonSelector: '.validate-button',
      validateOnBlur: true,
      validateOnInput: false,
      errorClass: 'field--error',
      fieldErrorSelector: '.input-validation', // Field-specific error containers
    });

    // Add custom validators specific to delivery form
    this.setupCustomValidators();

    // Handle schedule toggle functionality
    this.setupScheduleToggle();
  } 

  initEnterKeyNavigation() {
    const form = this.element.querySelector('form');
    if (!form) return;

    // Prevent default form submission
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      return false;
    });

    // Get all focusable elements in the delivery step (inputs, buttons, etc.)
    const getFocusableElements = () => {
      // Get inputs from form
      const formInputs = Array.from(form.querySelectorAll('input:not([type="hidden"]), textarea'))
        .filter(input => !input.disabled && !input.readOnly);
      
      // Get buttons from the delivery step footer
      const buttons = Array.from(this.element.querySelectorAll('.delivery-step__footer button:not([disabled])'));
      
      // Combine them in order: inputs first, then buttons
      return [...formInputs, ...buttons];
    };

    // Handle Enter key on form inputs
    form.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;

      const target = e.target;
      
      // Skip if not an input or textarea
      if (!target.matches('input, textarea')) return;

      // For textarea, allow Shift+Enter for new lines
      if (target.tagName === 'TEXTAREA' && e.shiftKey) {
        return;
      }

      // Prevent default form submission
      e.preventDefault();

      const focusableElements = getFocusableElements();
      const currentIndex = focusableElements.indexOf(target);

      if (currentIndex === -1) return;

      // Check if there's a next element
      const nextIndex = currentIndex + 1;
      
      if (nextIndex < focusableElements.length) {
        // Move to next element (field or button)
        const nextElement = focusableElements[nextIndex];
        
        // If next element is a button, focus it
        if (nextElement.tagName === 'BUTTON') {
          nextElement.focus();
        } else {
          // It's an input/textarea, just focus
          nextElement.focus();
        }
      } else {
        // This is the last field - trigger add to cart button
        const addToCartButton = this.element.querySelector('[data-action="delivery-add-to-cart"], [data-action="delivery-change-cart"]');
        if (addToCartButton) {
          addToCartButton.click();
        }
      }
    });

    // Also handle Enter key on focused buttons in the footer
    this.element.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      
      const target = e.target;
      
      // If it's a button in the footer and Enter is pressed, just let it trigger normally
      if (target.tagName === 'BUTTON' && target.closest('.delivery-step__footer')) {
        // Default button behavior will handle the click
        return;
      }
    });

    // Handle mobile keyboard scrolling - scroll focused input into view
    const formInputs = form.querySelectorAll('input, textarea');
    formInputs.forEach(input => {
      input.addEventListener('focus', () => {
        // Delay to allow keyboard to open
        setTimeout(() => {
          // Scroll the input into view with some padding
          input.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }, 300);
      });
    });
  }

  setupCustomValidators() {
    // Custom validator for delivery datetime
    this.validator.addValidator(
      'delivery_datetime',
      (value, field) => {
        const scheduleCheckbox = this.element.querySelector('#schedule_delivery');

        // If schedule is not checked, datetime is not required
        if (!scheduleCheckbox || !scheduleCheckbox.checked) {
          return true;
        }

        // If schedule is checked, datetime is required and must be in future
        if (!value) {
          return false;
        }

        return this.validator.isValidFutureDate(value);
      },
      window.translations?.products?.delivery_greeting_form?.datetime_required_error || 'Delivery date and time is required and must be in the future'
    );


    const formStrings = window.translations?.products?.delivery_greeting_form || {};

    // Format is checked by FormValidator itself for type="email" fields. FormValidator keeps a
    // single custom validator per field id (Map.set), so only the self-email rule lives here.
    this.validator.addValidator(
      'recipient_email',
      (value) => {
        if (!value) return true;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) return true;
        return !(window.customerEmail && value.toLowerCase() === window.customerEmail.toLowerCase());
      },
      formStrings.self_email_error || 'You cannot send a gift to yourself. Please enter a different email address.'
    );

    // Postnummer: digits only (five digits with an optional space after the third, or four digits)
    this.validator.addValidator(
      'recipient_zip',
      (value) => !value || /^(\d{3}\s?\d{2}|\d{4})$/.test(String(value).trim()),
      formStrings.zip_invalid || 'Enter a valid postal code.'
    );

  }

  setupScheduleToggle() {
    const scheduleCheckbox = this.element.querySelector('#schedule_delivery');
    const datetimeField = this.element.querySelector('#delivery_datetime');
    const datetimePlaceholder = this.element.querySelector('.datetime-placeholder');

    if (!scheduleCheckbox || !datetimeField) return;

    // Native picker: grey out past dates/times (datetime-local values are local YYYY-MM-DDTHH:MM)
    const refreshMinDatetime = () => {
      const pad = (n) => String(n).padStart(2, '0');
      const now = new Date();
      datetimeField.min = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    };
    refreshMinDatetime();
    datetimeField.addEventListener('focus', refreshMinDatetime);

    // Track if we have a value set
    let hasValue = false;

    // Handle placeholder visibility
    const updatePlaceholder = () => {
      if (!datetimePlaceholder) return;

      // Check both the actual value and our tracked state
      const fieldValue = datetimeField.value;
      hasValue = fieldValue && fieldValue.trim() !== '';

      if (hasValue) {
        datetimePlaceholder.classList.add('hidden');
      } else {
        datetimePlaceholder.classList.remove('hidden');
      }
    };

    // Make placeholder clickable and trigger input
    if (datetimePlaceholder) {
      datetimePlaceholder.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        datetimePlaceholder.classList.add('hidden');

        datetimeField.focus();
        setTimeout(() => {
          if (datetimeField.showPicker) {
            datetimeField.showPicker();
          }
        }, 0);
      });
    }

    // Hide placeholder on focus/click
    datetimeField.addEventListener('focus', () => {
      if (datetimePlaceholder) {
        datetimePlaceholder.classList.add('hidden');
      }
    });

    // Show placeholder on blur ONLY if empty
    datetimeField.addEventListener('blur', () => {
      setTimeout(() => {
        updatePlaceholder();
      }, 150);
    });

    // Update placeholder on change - keep hidden if value exists
    datetimeField.addEventListener('change', () => {
      hasValue = true;

      // Always hide placeholder after change
      if (datetimePlaceholder) {
        datetimePlaceholder.classList.add('hidden');
      }

      // Trigger validation after date selection
      setTimeout(() => {
        if (this.validator) {
          this.validator.updateButtonState();
        }
      }, 100);
    });

    // Update placeholder on input
    datetimeField.addEventListener('input', () => {
      hasValue = datetimeField.value && datetimeField.value.trim() !== '';
      if (hasValue && datetimePlaceholder) {
        datetimePlaceholder.classList.add('hidden');
      }
    });

    // Make entire input clickable
    datetimeField.addEventListener('click', function (e) {
      if (this.type === 'datetime-local') {
        setTimeout(() => {
          if (this.showPicker) {
            this.showPicker();
          }
        }, 0);
      }
    });

    let scheduleToggleInitialized = false;
    const handleScheduleToggle = () => {
      const datetimeFieldContainer = datetimeField.closest('.field');

      // GTM: custom_scheduled_delivery_toggled (only on user interaction, not init)
      if (window.dataLayer && scheduleToggleInitialized) {
        dataLayer.push({
          event: 'custom_scheduled_delivery_toggled',
          scheduled_delivery: scheduleCheckbox.checked,
          delivery_datetime: datetimeField.value || ''
        });
      }

      if (scheduleCheckbox.checked) {
        datetimeField.setAttribute('required', 'true');
      } else {
        datetimeField.removeAttribute('required');
        datetimeField.value = '';
        hasValue = false; // Reset tracked state
        datetimeFieldContainer.classList.remove('field--error');

        const validationEl = datetimeFieldContainer.querySelector('.input-validation');
        if (validationEl) {
          validationEl.textContent = '';
        }
      }

      // Update placeholder based on current value
      updatePlaceholder();
      this.validator.updateButtonState();
    };

    scheduleCheckbox.addEventListener('change', handleScheduleToggle);

    handleScheduleToggle();
    scheduleToggleInitialized = true;
    updatePlaceholder();
  }

  // Method to get form data - kept for convenience since it's commonly used
  getFormData() {
    return this.validator.getFormData();
  }
}
customElements.define("delivery-form", DeliveryForm);


// Improved version with lean popup and calculator for display - v11
class DeliveryPopup extends HTMLElement {
  static SELECTORS = {
    PACK_ALL_CHECKBOX: '#pack_all_vouchers',
    CURRENT_STEP: '.delivery-step[aria-expanded="true"]',
    STEP_CONTENT: '.delivery-popup__content',
    STEP_OPTIONS: '.delivery-step__options',
    SELECTED_OPTION: '[aria-selected="true"]',
    HIDDEN_INPUT: 'input[type="hidden"]',
    CART_FORM: 'form[data-type="add-to-cart-form"]'
  };

  static INITIAL_STATE = {
    choices: {},
    prices: { giftbox: 0, bow: 0, giftboxBase: 0 },
    quantity: 1,
    packAll: false,
    currentStep: 1,
    transitioning: false,
    editGroup: null,
    skipBowStep: false,
    shipping: {
      recipient_name: '',
      recipient_email: '',
      recipient_address: '',
      recipient_city: '',
      recipient_zip: '',
      greeting_message: '',
      schedule_delivery: false,
      delivery_datetime: ''
    }
  };

  // REMOVED: static STORAGE_KEY = 'delivery_popup_preferences';
  // We now use 'delivery_shipping_data' directly in loadAndPopulateFromLocalStorage()

  constructor() {
    super();
    this.element = this;
    this.state = { ...DeliveryPopup.INITIAL_STATE };
    this.delegateElement = new Delegate(this.element);
    new AddToCart(this.element);
    this._syncingState = false;

    this.delegateElement.on(
      "popup:closed",
      () => {
        // GTM: custom_delivery_popup_abandoned (only if not completed)
        if (window.dataLayer && !this._gtmPopupCompleted) {
          const productComp = document.querySelector('product-component');
          dataLayer.push({
            event: 'custom_delivery_popup_abandoned',
            delivery_type: this.state.choices.delivery_type || '',
            delivery_person: this.state.choices.delivery_person || '',
            last_step: this.state.currentStep,
            product_name: productComp ? productComp.dataset.title || '' : ''
          });
        }

        // Delay reset until after the popup close transition completes (300ms)
        // This prevents the flash of step 1 during the close animation
        setTimeout(() => {
          this.resetState();
        }, 400);
      }
    );
  }

  // ========================================
  // 🔧 LIFECYCLE METHODS
  // ========================================

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initialize() {
    this.loadStateFromDOM();
    this._initializeStateFromDOM();
    // loadPreferencesFromStorage() removed - we use simpler localStorage approach
    this.setupEventListeners();
    // setupFormCaptureListeners() removed - not needed anymore
    this.syncState();
  }

  cleanup() {
    this.delegateElement.off();
    if (window.globalPopup) {
      window.globalPopup.onPopupOpen = null;
    }
  }

  // ========================================
  // 📊 STATE MANAGEMENT
  // ========================================

  syncState() {
    if (this._syncingState) {
      console.warn('Sync already in progress, skipping to prevent loop');
      return;
    }

    this._syncingState = true;

    try {
      this.updateDOMDatasets();
      this.validateCurrentState();
      this.notifyExternalComponents();
      this.updateUIFromState();
      // savePreferencesToStorage() removed - we save to localStorage only on add to cart
    } catch (error) {
      console.error('Error during state sync:', error);
    } finally {
      this._syncingState = false;
    }
  }

  // REMOVED: savePreferencesToStorage() and loadPreferencesFromStorage()
  // These are replaced by the simpler loadAndPopulateFromLocalStorage() 
  // which is called on popup open and loads from 'delivery_shipping_data' key

  updateDOMDatasets() {
    const finalPrices = this.calculateFinalPrices();

    this.element.dataset.choices = JSON.stringify(this.state.choices);
    this.element.dataset.prices = JSON.stringify(this.state.prices);
    this.element.dataset.finalPrices = JSON.stringify(finalPrices);
    this.element.dataset.packAll = this.state.packAll.toString();
    this.element.dataset.currentStep = this.state.currentStep.toString();
    this.element.dataset.shipping = JSON.stringify(this.state.shipping);

    if (this.state.editGroup) {
      this.element.dataset.editGroup = this.state.editGroup;
    } else {
      delete this.element.dataset.editGroup;
    }
  }

  loadStateFromDOM() {
    try {
      const { dataset } = this.element;

      if (dataset.choices) {
        this.state.choices = JSON.parse(dataset.choices);
      }

      if (dataset.prices) {
        this.state.prices = JSON.parse(dataset.prices);
      }

      if (dataset.packAll !== undefined) {
        this.state.packAll = dataset.packAll === 'true';
      }

      if (dataset.currentStep) {
        this.state.currentStep = parseInt(dataset.currentStep);
      }

      if (dataset.originalQuantity) {
        this.state.quantity = parseInt(dataset.originalQuantity);
      }

      if (dataset.shipping) {
        this.state.shipping = {
          ...DeliveryPopup.INITIAL_STATE.shipping, // Ensure all fields exist
          ...JSON.parse(dataset.shipping)
        };
      }

      console.log('State loaded from DOM:', this.state);
    } catch (e) {
      console.warn('Failed to parse saved state:', e);
      this.resetToDefaultState();
    }
  }

  resetToDefaultState() {
    this.state = { ...DeliveryPopup.INITIAL_STATE };
  }

  validateCurrentState() {
    const warnings = [];

    if (this.state.currentStep > 1 && !this.state.choices.delivery_type) {
      warnings.push('Missing delivery_type selection');
    }

    if (this.state.currentStep > 1 && !this.state.choices.delivery_person) {
      warnings.push('Missing delivery_person selection');
    }

    if (this.state.prices.giftbox < 0 || this.state.prices.bow < 0) {
      warnings.push('Negative prices detected');
    }

    if (this.state.quantity < 1) {
      warnings.push('Invalid quantity');
    }

    if (warnings.length > 0) {
      console.warn('[DeliveryPopup] State validation warnings:', warnings);
    }
  }

  notifyExternalComponents() {
    const finalPrices = this.calculateFinalPrices();
    console.log('Popup notifying calculators with finalPrices:', finalPrices);
    console.log('Base prices:', this.state.prices);
    
    const stateEvent = new CustomEvent('delivery-popup-state-changed', {
      detail: {
        state: { ...this.state },
        finalPrices: finalPrices,
        element: this.element
      },
      bubbles: true
    });
    this.element.dispatchEvent(stateEvent);
  }

  // ========================================
  // 💰 PRICING LOGIC - SINGLE SOURCE OF TRUTH
  // ========================================

  /**
   * Extract pricing information from a selected option element
   * @returns {Object} { giftbox: number, bow: number } - base prices
   */
  extractPriceFromTarget(target, type) {
    if (!target) return null;

    const totalPrice = parseFloat(target.dataset.price);
    if (isNaN(totalPrice)) {
      console.warn('Invalid price value:', target.dataset.price);
      return null;
    }

    // GIFTBOX PRODUCT: Just the product price, no bow yet
    if (target.closest('.giftbox-option') || type === 'giftbox_product') {
      return {
        giftbox: totalPrice,
        bow: 0
      };
    }

    // BOW VARIANT: Total variant price is split into base + bow difference
    if (target.closest('.bow-option') || type === 'bow_variant') {
      const bowPriceDiff = parseFloat(target.dataset.bowPrice) || 0;
      return {
        giftbox: totalPrice - bowPriceDiff,  // Base variant price
        bow: bowPriceDiff                     // Bow upgrade cost
      };
    }

    return null;
  }

  /**
   * Calculate final prices with quantity multiplier
   * This is the ONLY method that should multiply prices
   */
  calculateFinalPrices() {
    const basePrices = this.state.prices;
    const quantity = this.state.packAll ? 1 : this.state.quantity;

    return {
      giftbox: basePrices.giftbox * quantity,
      bow: basePrices.bow * quantity,
      total: (basePrices.giftbox + basePrices.bow) * quantity,
      baseGiftbox: basePrices.giftbox,
      baseBow: basePrices.bow,
      quantity: quantity,
      packAll: this.state.packAll
    };
  }

  updateChoiceAndPrice(type, value, target = null, name = '') {
    this.state.choices[type] = value;

    if (name) {
      this.state.choices[`${type}_name`] = name;
    }

    // Update prices using unified extraction method
    if (target && target.dataset.price) {
      const prices = this.extractPriceFromTarget(target, type);
      if (prices) {
        this.state.prices.giftbox = prices.giftbox;
        this.state.prices.bow = prices.bow;
        console.log(`Updated prices for ${type}:`, prices);
      }
    }

    // Store product handle for giftbox products (needed for variant loading)
    if (type === 'giftbox_product' && target) {
      const slide = target.closest('.swiper-slide');
      if (slide && slide.dataset.productHandle) {
        this.state.choices.giftbox_product_handle = slide.dataset.productHandle;
        console.log('Stored product handle:', slide.dataset.productHandle);
      }
    }
  }

  updatePackAllState(packAll) {
    console.log('Updating packAll from', this.state.packAll, 'to', packAll);

    this.state.packAll = packAll;
    this.state.quantity = packAll ? 1 : parseInt(this.element.dataset.originalQuantity || '1');

    this.syncState();
  }

  updateShippingData(fieldName, value) {
    if (this.state.shipping.hasOwnProperty(fieldName)) {
      this.state.shipping[fieldName] = value;
      console.log(`Updated shipping.${fieldName}:`, value);
      // Note: We don't sync to localStorage here anymore
      // localStorage is only saved when adding to cart
    } else {
      console.warn(`Unknown shipping field: ${fieldName}`);
    }
  }

  //  NEW: Update multiple shipping fields at once
  updateShippingDataBatch(shippingData) {
    Object.entries(shippingData).forEach(([fieldName, value]) => {
      if (this.state.shipping.hasOwnProperty(fieldName)) {
        this.state.shipping[fieldName] = value;
      }
    });

    console.log('Updated shipping data batch:', shippingData);
    // Note: We don't sync to localStorage here anymore
    // localStorage is only saved when adding to cart
  }

  // REMOVED: getShippingData() - no longer needed
  // We don't pass state data between DeliveryForm and DeliveryPopup anymore

  // ========================================
  // 🎨 UI RESTORATION & UPDATES
  // ========================================

  updateUIFromState() {
    const currentStep = this.element.querySelector(DeliveryPopup.SELECTORS.CURRENT_STEP);
    if (currentStep) {
      this.restoreChoicesInStep(currentStep, true);
      this.restorePackAllUI();
    }
  }

  restoreChoicesInStep(stepElement, duringSync = false) {
    Object.entries(this.state.choices).forEach(([type, value]) => {
      this.restoreSingleChoice(stepElement, type, value, duringSync);
    });

    if (!duringSync && !this._syncingState) {
      this.syncState();
    }
  }

  restoreSingleChoice(stepElement, type, value, duringSync) {
    const optionElement = stepElement.querySelector(`[data-info="${type}-${value}"]`);
    if (!optionElement) return;

    const container = optionElement.closest(DeliveryPopup.SELECTORS.STEP_OPTIONS);
    if (!container) return;

    // Clear previous selections
    container.querySelectorAll(DeliveryPopup.SELECTORS.SELECTED_OPTION)
      .forEach(el => el.setAttribute('aria-selected', 'false'));

    // Set current selection
    optionElement.setAttribute('aria-selected', 'true');

    // Update hidden input
    const input = container.querySelector(DeliveryPopup.SELECTORS.HIDDEN_INPUT);
    if (input) input.value = value;

    // Restore price using unified extraction method
    if (optionElement.dataset.price && !duringSync) {
      const prices = this.extractPriceFromTarget(optionElement, type);
      if (prices) {
        this.state.prices.giftbox = prices.giftbox;
        this.state.prices.bow = prices.bow;
      }
    }
  }

  restorePackAllUI() {
    // tu je nes krivo
    const packAllElement = this.element.querySelector(DeliveryPopup.SELECTORS.PACK_ALL_CHECKBOX);

    if (packAllElement && packAllElement.type === 'checkbox') {
      packAllElement.checked = this.state.packAll;
    } else if (!packAllElement) {
      console.warn('PackAll UI element not found');
    }
  }


  _initializeStateFromDOM() {
    const preselectedOptions = this.element.querySelectorAll(DeliveryPopup.SELECTORS.SELECTED_OPTION);

    if (preselectedOptions.length === 0) return;

    console.log('Found pre-selected options, initializing state...');

    preselectedOptions.forEach(option => {
      if (option.dataset.info) {
        const [type, value] = option.dataset.info.split('-');
        // Use your existing method to update the state and price
        this.updateChoiceAndPrice(type, value, option);
      }
    });
  }

  // ========================================
  // 🚶 NAVIGATION & STEP MANAGEMENT
  // ========================================

  async navigateToStep(direction) {
    if (this.state.transitioning) return;

    const newStep = this.calculateNextStep(direction);
    await this.openStep(newStep);
  }

  calculateNextStep(direction) {
    let newStep = direction === 'previous'
      ? this.state.currentStep - 1
      : this.state.currentStep + 1;

    if (newStep === 2) {
      console.log(this.state.choices, " choices ");
      newStep = this.state.choices.delivery_type === 'giftbox' ? "2_1" : "2_2";
    }

    // Handle skipping step 3 (bow selection) if giftbox has no bow variants
    if (this.state.skipBowStep) {
      // Going forward from step 2_1 to step 3 - skip to step 4
      if (direction === 'next' && (this.state.currentStep === '2_1' || this.state.currentStep === 2)) {
        console.log('Skipping step 3 (no bow variants), going to step 4');
        newStep = 4;
      }
      // Going backward from step 4 to step 3 - skip to step 2_1
      if (direction === 'previous' && this.state.currentStep === 4 && newStep === 3) {
        console.log('Skipping step 3 (no bow variants), going back to step 2_1');
        newStep = '2_1';
      }
    }

    return newStep;
  }

  async openStep(stepNumber) {


    const currentStepEl = this.element.querySelector(DeliveryPopup.SELECTORS.CURRENT_STEP);
    const newStepEl = this.getOrCreateStep(stepNumber);

    if (!newStepEl || this.state.transitioning) return;

    this.state.transitioning = true;
    this.updateCurrentStepNumber(stepNumber);

    await this.performStepTransition(currentStepEl, newStepEl);

    this.state.transitioning = false;
    this.syncState();
  }

  // REMOVED: setupFormCaptureListeners() - dead code
  // We don't need to capture state on blur/unload anymore
  // State is only saved to localStorage when adding to cart

  updateCurrentStepNumber(stepNumber) {
    if (stepNumber.toString().includes('_')) {
      this.state.currentStep = parseInt(stepNumber.split('_')[0]);
      console.log(this.state.currentStep, " step ");
    } else {
      this.state.currentStep = stepNumber;
      console.log(this.state.currentStep, " step ");
    }
  }

  getOrCreateStep(stepNumber) {
    let step = this.element.querySelector(`.delivery-step[data-step="${stepNumber}"]`);

    if (!step) {
      step = this.createStepFromTemplate(stepNumber);
    }

    return step;
  }

  createStepFromTemplate(stepNumber) {
    const template = this.element.querySelector(`template[data-step="${stepNumber}"]`);

    console.log(stepNumber, " number step ");
    console.log(template, " template ");

    if (!template) return null;

    const step = template.content.cloneNode(true).querySelector('.delivery-step');
    
    // Set the data-step attribute on the cloned step element
    if (step) {
      step.dataset.step = stepNumber;
      console.log('Set data-step attribute:', stepNumber);
    }

    const preselectedOptions = step.querySelectorAll('[aria-selected="true"]');
    console.log(`Preselected options found for step ${stepNumber}:`, preselectedOptions.length);
    if (preselectedOptions.length > 0) {
      preselectedOptions.forEach(option => {
        if (option.dataset.info) {
          const [type, value] = option.dataset.info.split('-');
          if (!this.state.choices[type]) {
            console.log(`State for '${type}' is empty. Setting default from template.`);
            this.updateChoiceAndPrice(type, value, option);
          }
        }
      });
    }

    this.filterStepByChoices(step);
    this.element.querySelector(DeliveryPopup.SELECTORS.STEP_CONTENT).appendChild(step);
    this.restoreChoicesInStep(step, true);
    this.restorePackAllUI();

    // Update button text based on delivery_person
    if (stepNumber === '2_2') {
      this.updateStepButtonText(step);
    }

    return step;
  }

  filterStepByChoices(stepElement) {
    // Build allowlist from multiple sources so defaults are respected before any clicks
    const allowed = new Set();

    // 1) From current state choices
    Object.entries(this.state?.choices || {}).forEach(([key, value]) => {
      if (!value) return;
      allowed.add(String(value));
      allowed.add(`${key}-${value}`);
    });

    // 2) From preselected options inside this step (initial DOM defaults)
    const preselectedLocal = stepElement.querySelectorAll('[aria-selected="true"][data-info]');
    preselectedLocal.forEach(opt => {
      const info = opt.getAttribute('data-info'); // format: key-value
      if (!info) return;
      const [key, value] = info.split('-');
      if (!value) return;
      allowed.add(String(value));
      if (key) allowed.add(`${key}-${value}`);
    });

    // 3) From globally preselected options in the popup (e.g., step 1 selections)
    const preselectedGlobal = this.element.querySelectorAll('[aria-selected="true"][data-info]');
    preselectedGlobal.forEach(opt => {
      const info = opt.getAttribute('data-info');
      if (!info) return;
      const [key, value] = info.split('-');
      if (!value) return;
      allowed.add(String(value));
      if (key) allowed.add(`${key}-${value}`);
    });

    // 4) From hidden inputs mirroring current selections (delivery_type/person)
    const hiddenChoiceInputs = this.element.querySelectorAll(
      'input[type="hidden"][name="delivery_person"], input[type="hidden"][name="delivery_type"]'
    );
    hiddenChoiceInputs.forEach(input => {
      const name = input.getAttribute('name');
      const value = (input.value || '').trim();
      if (!value) return;
      allowed.add(String(value));
      if (name) allowed.add(`${name}-${value}`);
    });

    // If we couldn't resolve any allowed values yet, do nothing to avoid removing UI prematurely
    if (allowed.size === 0) return;

    // Remove only elements whose substep isn't allowed
    stepElement.querySelectorAll('[data-substep]').forEach(el => {
      const sub = el.dataset.substep;
      if (!allowed.has(sub)) {
        el.remove();
      }
    });
  }

  async performStepTransition(currentStep, newStep) {
    console.log('performStepTransition called, newStep.dataset.step:', newStep?.dataset?.step);
    
    if (!currentStep) {
      this.showStep(newStep);
      
      // Still need to handle step-specific logic even without transition
      if (newStep.dataset.step === '2_2') {
        this.updateStepButtonText(newStep);
      }
      
      // Load bow variants when entering step 3
      console.log('Checking if step is 3:', newStep.dataset.step, newStep.dataset.step === '3');
      if (newStep.dataset.step === '3') {
        console.log('Loading variants for step 3 (no previous step)');
        try {
          await this.loadBowVariants(newStep);
        } catch (error) {
          console.error('Error in loadBowVariants:', error);
        }
      }
      
      return;
    }

    this.element.classList.add('switching');
    await this.waitForTransition(this.element, 'opacity');

    this.hideStep(currentStep);
    this.showStep(newStep);

    // Update button text after showing new step
    if (newStep.dataset.step === '2_2') {
      this.updateStepButtonText(newStep);
    }

    // Load bow variants when entering step 3
    console.log('Checking if step is 3 (with transition):', newStep.dataset.step, newStep.dataset.step === '3');
    if (newStep.dataset.step === '3') {
      console.log('Loading variants for step 3 (with transition)');
      try {
        await this.loadBowVariants(newStep);
      } catch (error) {
        console.error('Error in loadBowVariants:', error);
      }
    }

    this.element.classList.remove('switching');
  }

  hideStep(stepElement) {
    stepElement.setAttribute('aria-expanded', 'false');
    stepElement.setAttribute('aria-hidden', 'true');
    if (stepElement.dataset.step !== '1') {
      stepElement.remove();
    }
  }

  showStep(stepElement) {
    stepElement.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      stepElement.setAttribute('aria-expanded', 'true');
      
      // SIMPLE: Populate all visible fields when step is shown
      setTimeout(() => {
        this.loadAndPopulateFromLocalStorage();
      }, 100);
    });
  }

  waitForTransition(element, property) {
    return new Promise(resolve => {
      const handler = (e) => {
        if (e.propertyName === property && e.target === element) {
          element.removeEventListener('transitionend', handler);
          resolve();
        }
      };
      element.addEventListener('transitionend', handler);

      setTimeout(() => {
        element.removeEventListener('transitionend', handler);
        resolve();
      }, 1000);
    });
  }

  async loadBowVariants(stepElement) {
    console.log('Loading bow variants for selected giftbox');
    
    // Get product handle from state (stored when giftbox was selected)
    let productHandle = this.state.choices.giftbox_product_handle;
    console.log('Product handle from state:', productHandle);
    
    // Fallback: try to find it from the DOM if not in state (for preselected options)
    if (!productHandle) {
      const giftboxProductId = this.state.choices.giftbox_product;
      console.log('No handle in state, trying to find from product ID:', giftboxProductId);
      
      if (giftboxProductId) {
        // Look for the giftbox slide that might still be in DOM
        const selectedGiftbox = this.element.querySelector(`.swiper-slide[data-product-id="${giftboxProductId}"]`);
        if (selectedGiftbox) {
          productHandle = selectedGiftbox.dataset.productHandle;
          // Store it for next time
          this.state.choices.giftbox_product_handle = productHandle;
          console.log('Found and stored product handle:', productHandle);
        }
      }
    }
    
    if (!productHandle) {
      console.warn('Could not determine product handle for loading variants');
      const wrapper = stepElement.querySelector('#bow-variants-wrapper');
      if (wrapper) {
        wrapper.innerHTML = '<div class="swiper-slide"><p class="u-s3">Please select a giftbox first</p></div>';
      }
      return;
    }

    console.log('Loading variants for product:', productHandle);

    // Show loading indicator
    const loadingIndicator = stepElement.querySelector('.bow-variants-loading');
    const variantsContainer = stepElement.querySelector('.bow-variants-container');
    if (loadingIndicator) loadingIndicator.style.display = 'block';
    if (variantsContainer) variantsContainer.style.display = 'none';

    try {
      // Fetch product data with variants
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Failed to fetch product data');
      
      const productData = await response.json();
      console.log('Product data loaded:', productData);

      // Get the wrapper to insert variants
      const wrapper = stepElement.querySelector('#bow-variants-wrapper');
      if (!wrapper) {
        console.warn('Bow variants wrapper not found');
        return;
      }

      // Clear existing content
      wrapper.innerHTML = '';

      // Create variant options
      const availableVariants = productData.variants.filter(v => v.available);
      
      // If there are no variants or only 1 variant (no bow options), skip to step 4
      if (availableVariants.length === 0) {
        console.log('No available variants for this giftbox, skipping step 3');
        
        // Mark that step 3 should be skipped
        this.state.skipBowStep = true;
        
        // Hide step 3 immediately to prevent flash
        stepElement.style.display = 'none';
        
        // Auto-navigate to step 4 immediately
        setTimeout(() => {
          this.navigateToStep('next');
        }, 50);
        return;
      }
      
      // If only 1 variant exists, it means no bow options (just the base product)
      // Auto-select it and skip to shipping
      if (availableVariants.length === 1) {
        console.log('Only 1 variant available (no bow options), auto-selecting and skipping to step 4');
        const variant = availableVariants[0];
        
        // Set the variant in state
        this.state.choices.bow_variant = variant.id;
        this.state.choices.bow_variant_name = variant.title;
        this.state.choices.giftbox_variant = variant.id;
        this.state.prices.giftbox = variant.price;
        
        // Mark that step 3 should be skipped
        this.state.skipBowStep = true;
        
        this.syncState();
        
        // Hide step 3 immediately to prevent flash
        stepElement.style.display = 'none';
        
        // Auto-navigate to step 4 immediately
        setTimeout(() => {
          this.navigateToStep('next');
        }, 50);
        return;
      }
      
      // If we reach here, there are multiple variants - bow step is needed
      this.state.skipBowStep = false;

      // Store the base variant (first available) price for calculating bow differences
      const baseVariantPrice = availableVariants[0].price;
      this.state.prices.giftboxBase = baseVariantPrice;
      console.log('Base giftbox variant price:', baseVariantPrice);

      // Check if there's a previously selected bow variant in state
      const previouslySelectedVariantId = this.state.choices.bow_variant || this.state.choices.giftbox_variant;
      console.log('Previously selected variant ID:', previouslySelectedVariantId);

      availableVariants.forEach((variant, index) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        
        // Select the previously chosen variant, or first one if none selected
        const isSelected = previouslySelectedVariantId 
          ? (variant.id === parseInt(previouslySelectedVariantId))
          : (index === 0);
        
        // Calculate the bow price as difference from base variant
        const bowPriceDiff = variant.price - baseVariantPrice;
        const formattedBowPrice = this.formatMoney(Math.abs(bowPriceDiff));
        const priceDisplay = bowPriceDiff > 0 ? `+${formattedBowPrice}` : (bowPriceDiff < 0 ? `-${formattedBowPrice}` : formattedBowPrice);

        slide.innerHTML = `
          <div data-info="bow_variant-${variant.id}" 
               data-action="select-option" 
               data-price="${variant.price}"
               data-bow-price="${bowPriceDiff}"
               class="delivery-option bow-option" 
               aria-selected="${isSelected}">
            ${variant.featured_image ? `
              <div class="AspectRatio AspectRatio--withFallback delivery-option__image" style="--aspect-ratio: 1/1;">
                <img src="${variant.featured_image.src}" alt="${variant.title}" loading="lazy">
              </div>
            ` : ''}
            <div class="delivery-popup__option-info">
              <div class="delivery-popup__option-info-title">
                <span class="u-s3">${variant.title}</span>
              </div>
              <span class="u-s3 secondary-text">${priceDisplay}</span>
            </div>
          </div>
        `;

        wrapper.appendChild(slide);

        // Set selected variant in state
        if (isSelected) {
          const bowOption = slide.querySelector('.delivery-option');
          this.updateChoiceAndPrice('bow_variant', variant.id, bowOption, variant.title);
          // Store the giftbox variant that will be added to cart (giftbox with bow)
          this.state.choices.giftbox_variant = variant.id;
          // Update giftbox price to base variant price
          this.state.prices.giftbox = baseVariantPrice;
          // Store bow price as the difference
          this.state.prices.bow = bowPriceDiff;
          console.log('Restoring selection - Giftbox base:', baseVariantPrice, 'Bow diff:', bowPriceDiff, 'Variant:', variant.id);
        }
      });

      // Reinitialize swiper and navigate to selected slide
      const swiperElement = stepElement.querySelector('.bow-variants-container');
      if (swiperElement && swiperElement.swiper) {
        swiperElement.swiper.update();
        
        // Navigate to the selected slide
        const selectedSlideIndex = availableVariants.findIndex(v => 
          v.id === parseInt(previouslySelectedVariantId || availableVariants[0].id)
        );
        if (selectedSlideIndex >= 0) {
          swiperElement.swiper.slideTo(selectedSlideIndex, 0); // 0 = no animation
          console.log('Navigated swiper to slide', selectedSlideIndex);
        }
      }

      this.syncState();

    } catch (error) {
      console.error('Error loading bow variants:', error);
      const wrapper = stepElement.querySelector('#bow-variants-wrapper');
      if (wrapper) {
        wrapper.innerHTML = '<div class="swiper-slide"><p class="u-s3">Error loading bow options</p></div>';
      }
    } finally {
      // Hide loading indicator
      if (loadingIndicator) loadingIndicator.style.display = 'none';
      if (variantsContainer) variantsContainer.style.display = '';
    }
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat || '{{amount}}');
    }
    // Fallback to simple formatting
    return `${(cents / 100).toFixed(2)} kr`;
  }

  // ========================================
  // 🎯 EVENT HANDLING
  // ========================================

  setupEventListeners() {
    this.delegateElement.on('click', '[data-action]', this.handleAction.bind(this));

    if (window.globalPopup) {
      window.globalPopup.onPopupOpen = (popup, triggerButton) => {
        if (popup === this.element) this.handlePopupOpen(triggerButton);
      };
    }
  }

  handleAction(event, target) {
    const action = target.dataset.action;
    // GTM: Track delivery popup completed (add-to-cart / change-cart)
    if ((action === 'delivery-add-to-cart' || action === 'delivery-change-cart') && window.dataLayer) {
      this._gtmPopupCompleted = true;
      dataLayer.push({
        event: 'custom_delivery_popup_completed',
        delivery_type: this.state.choices.delivery_type || '',
        delivery_person: this.state.choices.delivery_person || '',
        giftbox_product: this.state.choices.giftbox_product || '',
        total_price: (this.state.prices.giftbox || 0) + (this.state.prices.bow || 0)
      });
    }

    const actionHandlers = {
      'select-option': () => this.handleOptionSelection(target),
      'toggle-step': () => {
        const direction = target.dataset.direction;
        // GTM: custom_delivery_popup_step_changed
        if (window.dataLayer) {
          const stepNames = {'1':'delivery_options','2':'giftbox_or_voucher','2_1':'giftbox_selection','2_2':'voucher_form','3':'bow_selection','4':'shipping_form'};
          const fromStep = this.state.currentStep;
          const fromStepNum = typeof fromStep === 'string' ? parseFloat(fromStep) : fromStep;
          const toStep = direction === 'next' ? fromStepNum + 1 : fromStepNum - 1;
          dataLayer.push({ event: 'custom_delivery_popup_step_changed', from_step: String(fromStep), from_step_name: stepNames[String(fromStep)] || 'step_'+fromStep, to_step: String(toStep), direction: direction });
        }
        this.navigateToStep(direction);
      },
      // Close the delivery popup using the global popup manager
      'toggle-popup': () => {
        console.log('toggle-popup action called');
        console.trace('toggle-popup trace');
        // Prevent the global body delegate from double-toggling
        try { event.stopPropagation(); } catch (_) { }
        if (window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          window.globalPopup.closePopup(this.element);
        } else if (window.globalPopup && typeof window.globalPopup.closeAllPopups === 'function') {
          // Fallback: close all popups if specific close is unavailable
          window.globalPopup.closeAllPopups();
        } else {
          // Last resort: hide the element
          this.element.setAttribute('aria-expanded', 'false');
          this.element.setAttribute('aria-hidden', 'true');
        }
      }
    };

    const handler = actionHandlers[action];
    if (handler) {
      event.preventDefault();
      handler();
    }
  }

  // REMOVED: captureCurrentFormState() and getFieldNameFromInput() - dead code
  // We don't need to capture form state anymore, it's saved only on add to cart

  // Method to handle dynamic button text updates
  updateStepButtonText(stepElement) {
    // Read delivery_person from DOM instead of state
    const deliveryPersonInput = this.element.querySelector('input[type="hidden"][name="delivery_person"]');
    const deliveryPerson = deliveryPersonInput?.value;

    if (!deliveryPerson) return;

    // Update button text
    const submitButton = stepElement.querySelector('[data-action="delivery-add-to-cart"], [data-action="delivery-change-cart"]');

    if (submitButton) {
      const buttonText = submitButton.querySelector('.u-pb1');
      if (buttonText) {
        // Pass translations from Liquid to JS
        const translations = {
          add_to_cart: this.element.dataset.translationCheckout,
          checkout: this.element.dataset.translationAddToCart
        };

        buttonText.textContent = deliveryPerson === 'buyer'
          ? translations.add_to_cart
          : translations.checkout;
      }
    }
  }

  handleOptionSelection(target) {
    const [type, value] = target.dataset.info.split('-');
    const container = target.closest(DeliveryPopup.SELECTORS.STEP_OPTIONS);

    // CRITICAL: Check if this is a delivery type change that requires state reset
    if (type === 'delivery_type' && this.state.choices.delivery_type && this.state.choices.delivery_type !== value) {
      console.log(`Delivery type changed from ${this.state.choices.delivery_type} to ${value} - resetting state`);

      // Reset all state except the new delivery type choice
      const newState = { ...DeliveryPopup.INITIAL_STATE };
      newState.choices.delivery_type = value;
      newState.quantity = this.state.quantity; // Preserve quantity
      newState.editGroup = this.state.editGroup; // Preserve edit group if exists

      // SMART APPROACH: Preserve ALL shipping data across delivery type changes
      // The form restoration will intelligently populate only relevant fields
      // This allows users to switch back and forth without losing any data
      if (this.state.shipping) {
        newState.shipping = { ...this.state.shipping };
        console.log('Delivery type changed: preserved all shipping data for smart restoration');
      }

      this.state = newState;
      console.log('State reset for delivery type change:', this.state);

      // Clear any shipping form data
      this.clearShippingFormData();

      // Clear DOM datasets to ensure clean state
      this.clearDOMDatasets();
    }

    this.updateOptionUI(container, target);
    this.updateHiddenInput(container, value);

    const nameElement = target.querySelector('.delivery-popup__option-info-title .u-s3');
    const name = nameElement ? nameElement.textContent.trim() : '';

    // Pass the extracted name when updating the state
    this.updateChoiceAndPrice(type, value, target, name);

    // GTM: Track option selections
    if (window.dataLayer) {
      const productComp = document.querySelector('product-component');
      const productName = productComp ? productComp.dataset.title || '' : '';

      if (type === 'delivery_type') {
        dataLayer.push({ event: 'custom_delivery_type_selected', delivery_type: value, product_name: productName });
      } else if (type === 'delivery_person') {
        dataLayer.push({ event: 'custom_delivery_person_selected', delivery_person: value, delivery_type: this.state.choices.delivery_type || '' });
      } else if (type === 'giftbox_product') {
        dataLayer.push({ event: 'custom_giftbox_selected', giftbox_product_name: name, giftbox_price: this.state.prices.giftbox || 0, delivery_person: this.state.choices.delivery_person || '' });
      } else if (type === 'bow_variant') {
        dataLayer.push({ event: 'custom_bow_selected', bow_variant_name: name, bow_price: this.state.prices.bow || 0 });
      }
    }

    // Special handling for giftbox product selection
    if (type === 'giftbox_product') {
      // Reset skipBowStep when a new giftbox is selected
      this.state.skipBowStep = false;
    }

    // Special handling for bow variant selection
    if (type === 'bow_variant') {
      // The bow variant IS the giftbox variant in the new system
      this.state.choices.giftbox_variant = value;
    }

    // Note: No special handling needed for delivery_person changes
    // Fields will auto-populate from localStorage when navigating to form steps

    this.syncState();
  }

  updateOptionUI(container, selectedTarget) {
    container.querySelectorAll(DeliveryPopup.SELECTORS.SELECTED_OPTION)
      .forEach(el => el.setAttribute('aria-selected', 'false'));
    selectedTarget.setAttribute('aria-selected', 'true');
  }

  updateHiddenInput(container, value) {
    const input = container.querySelector(DeliveryPopup.SELECTORS.HIDDEN_INPUT);
    if (input) input.value = value;
  }

  // REMOVED: autopopulateLoggedInCustomerData(), checkAndAutopopulateForStep(), 
  // triggerFormRestoration(), and populateFormWithCustomerData()
  // All replaced by the simpler loadAndPopulateFromLocalStorage() method

  handlePopupOpen(triggerButton) {
    const formFromTrigger =
      triggerButton &&
      typeof triggerButton.closest === 'function' &&
      triggerButton.closest(DeliveryPopup.SELECTORS.CART_FORM);
    this._cartFormFromTrigger = formFromTrigger || null;
    this.initializeQuantityData();
    this._cartFormFromTrigger = null;
    this.reconcilePackAllState();
    this.syncState();
    this._gtmPopupCompleted = false;
    this._gtmShippingFormStarted = false;

    // GTM: custom_delivery_popup_opened
    if (window.dataLayer) {
      const productComp = document.querySelector('product-component');
      const variantId = productComp ? Number(productComp.dataset.firstVariantId) || 0 : 0;
      let productPrice = 0;
      try {
        const metaProduct = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product;
        if (metaProduct && metaProduct.variants) {
          const variant = metaProduct.variants.find(v => v.id === variantId) || metaProduct.variants[0];
          if (variant) productPrice = variant.price / 100;
        }
      } catch (_) {}
      dataLayer.push({
        event: 'custom_delivery_popup_opened',
        product_name: productComp ? productComp.dataset.title || '' : '',
        product_id: productComp ? Number(productComp.dataset.productId) || 0 : 0,
        product_price: productPrice,
        variant_id: variantId,
        default_delivery_type: this.state.choices.delivery_type || 'voucher',
        default_delivery_person: this.state.choices.delivery_person || 'buyer'
      });
    }

    // SIMPLE: Load shipping data from localStorage and populate all visible fields
    setTimeout(() => {
      this.loadAndPopulateFromLocalStorage();
    }, 200);
  }

  // SIMPLE: Load from localStorage and populate whatever fields exist
  loadAndPopulateFromLocalStorage() {
    try {
      // Check if we're in edit mode (editing existing cart item)
      if (this.state.editGroup) {
        console.log('Edit mode detected - using state shipping data, not localStorage');
        // Use shipping data from state (loaded from cart item)
        const shippingData = this.state.shipping;
        
        if (!shippingData || Object.keys(shippingData).length === 0) {
          console.log('No shipping data in state');
          return;
        }

        console.log('Using shipping data from state:', shippingData);
        
        // Populate ALL visible fields with state data (including greeting_message for editing)
        Object.entries(shippingData).forEach(([fieldName, value]) => {
          if (!value && value !== false) return; // Skip empty values
          
          const input = this.element.querySelector(`#${fieldName}`);
          if (input) {
            if (input.type === 'checkbox') {
              input.checked = Boolean(value);
            } else {
              input.value = String(value);
            }
            // Trigger input event for validation
            input.dispatchEvent(new Event('input', { bubbles: true }));
            console.log(`Populated ${fieldName}:`, value);
          }
        });

        console.log('Field population from state complete');
        return;
      }

      // Not in edit mode - use localStorage
      const saved = localStorage.getItem('delivery_shipping_data');
      if (!saved) {
        console.log('No saved shipping data found in localStorage');
        return;
      }

      const shippingData = JSON.parse(saved);
      console.log('Loaded shipping data from localStorage:', shippingData);

      // Check current delivery choices
      const deliveryType = this.state.choices.delivery_type; // 'giftbox' or 'voucher'
      const deliveryPerson = this.state.choices.delivery_person; // 'buyer' or 'gift_receiver'

      console.log(`Current choices - Type: ${deliveryType}, Person: ${deliveryPerson}`);

      // For giftbox delivery to someone else, don't auto-populate shipping fields
      // They should enter recipient's fresh address, not saved data
      if (deliveryType === 'giftbox' && deliveryPerson === 'gift_receiver') {
        console.log('Giftbox for someone else - skipping auto-population');
        return;
      }

      // Populate ALL visible fields with saved data (except greeting_message - too personal)
      Object.entries(shippingData).forEach(([fieldName, value]) => {
        if (!value && value !== false) return; // Skip empty values
        if (fieldName === 'greeting_message') return; // Never populate greeting message
        
        const input = this.element.querySelector(`#${fieldName}`);
        if (input) {
          if (input.type === 'checkbox') {
            input.checked = Boolean(value);
          } else {
            input.value = String(value);
          }
          // Trigger input event for validation
          input.dispatchEvent(new Event('input', { bubbles: true }));
          console.log(`Populated ${fieldName}:`, value);
        }
      });

      console.log('Field population from localStorage complete');
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
    }
  }

  initializeQuantityData() {
    const cartForm =
      this._cartFormFromTrigger ||
      document.querySelector(DeliveryPopup.SELECTORS.CART_FORM);
    console.log('[AddToCartDelivery] cartForm:', cartForm);
    if (!cartForm) return;

    const formId = cartForm.getAttribute('id');

    const quantity = cartForm.querySelector('.pm__quantity-input');
    const variantId = cartForm.querySelector('input[name="id"]')?.value;

    // Capture _giftbox property if present
    const giftboxInput = cartForm.querySelector('input[name="properties[_giftbox]"]');
    const isGiftbox = giftboxInput?.value === 'true';

    // Capture collection handle from product-collection-grid if present
    const productCollectionGrid = document.querySelector('product-collection-grid');
    const collectionHandle = productCollectionGrid?.dataset.collectionHandle || '';

    console.log("variantId", variantId);
    console.log("isGiftbox", isGiftbox);
    console.log("collectionHandle", collectionHandle);

    this.element.dataset.originalQuantity = quantity?.value || '1';
    this.element.dataset.variantId = variantId;
    this.element.dataset.isGiftbox = isGiftbox;
    this.element.dataset.collectionHandle = collectionHandle;
    console.log('this.element', this.element);
    const originalQuantity = parseInt(this.element.dataset.originalQuantity || '1');
    this.state.quantity = this.state.packAll ? 1 : originalQuantity;
  }

  reconcilePackAllState() {
    if (this.element.dataset.packAll === undefined) {
      this.state.packAll = false;
    }
  }

  // ========================================
  // 🔧 PUBLIC API METHODS
  // ========================================

  getState() {
    return {
      ...this.state,
      choices: { ...this.state.choices },
      prices: { ...this.state.prices },
      shipping: { ...this.state.shipping }
    };
  }

  getFinalPrices() {
    return this.calculateFinalPrices();
  }

  updatePackAll(packAll) {
    this.updatePackAllState(packAll);
  }

  updateState(updates) {
    Object.assign(this.state, updates);
    this.syncState();
  }

  resetState() {
    this.state = { ...DeliveryPopup.INITIAL_STATE };
    this.clearDOMDatasets();
    this.clearShippingFormData();
    // Ensure UI is reset to the first step so next open starts at step 1
    this.resetUIToFirstStep();
    this.syncState();
  }

  clearDOMDatasets() {
    // Clear DOM datasets
    delete this.element.dataset.choices;
    delete this.element.dataset.prices;
    delete this.element.dataset.finalPrices;
    delete this.element.dataset.packAll;
    delete this.element.dataset.currentStep;
    delete this.element.dataset.shipping;
    delete this.element.dataset.editGroup;
  }

  clearShippingFormData() {
    // Find and clear all shipping form inputs in the popup
    const shippingInputs = this.element.querySelectorAll(`
      #recipient_name,
      #recipient_email, 
      #recipient_address,
      #recipient_city,
      #recipient_zip,
      #greeting_message,
      #schedule_delivery,
      #delivery_datetime
    `);

    console.log(`Clearing ${shippingInputs.length} shipping form inputs`);

    shippingInputs.forEach(input => {
      if (input.type === 'checkbox') {
        input.checked = false;
      } else {
        input.value = '';
      }
    });

    // Also clear any character counters or validation messages
    const charCounters = this.element.querySelectorAll('.char-count');
    charCounters.forEach(counter => {
      counter.textContent = '0/500'; // Reset to default
    });
  }

  // Ensure only step 1 is visible/present and marked as expanded
  resetUIToFirstStep() {
    try {
      // Remove all steps except step 1
      const allSteps = this.element.querySelectorAll('.delivery-step');
      allSteps.forEach(step => {
        if (step.dataset.step !== '1') {
          // Use existing removal behavior
          step.remove();
        }
      });

      // Make sure step 1 exists, create if needed
      let stepOne = this.element.querySelector('.delivery-step[data-step="1"]');
      if (!stepOne) {
        stepOne = this.getOrCreateStep(1);
      }
      if (stepOne) {
        stepOne.setAttribute('aria-hidden', 'false');
        stepOne.setAttribute('aria-expanded', 'true');
      }

      // Sync internal step number to 1 for consistency
      this.updateCurrentStepNumber(1);
    } catch (e) {
      console.warn('Failed to reset UI to first step:', e);
    }
  }

  async restoreAndOpen(editData) {
    console.log("=== STARTING POPUP RESTORATION ===");
    console.log("Edit data:", editData);

    // Set restoration flag to prevent auto-close
    this._isRestoring = true;

    // 1. Reset state data but DON'T reset UI (prevent flash of step 1)
    this.state = { ...DeliveryPopup.INITIAL_STATE };
    this.clearDOMDatasets();
    this.clearShippingFormData();
    // Skip this.resetUIToFirstStep() to prevent flash
    
    // 2. Set the main product data
    this.element.dataset.originalQuantity = editData.quantity;
    this.element.dataset.variantId = editData.variantId;
    this.element.dataset.isGiftbox = editData.isGiftbox || 'false';
    this.element.dataset.collectionHandle = editData.collectionHandle || '';
    this.state.editGroup = editData.editGroup;

    // 3. Update the popup's internal state INCLUDING shipping data
    this.state = {
      ...this.state,
      ...editData.state,
      shipping: {
        ...DeliveryPopup.INITIAL_STATE.shipping,
        ...editData.shipping
      }
    };
    
    console.log("State after merge:", this.state);
    console.log("Shipping data after merge:", this.state.shipping);

    // 4. Determine the target step based on the restored state
    const targetStepNumber = editData.state.choices.delivery_type === 'giftbox' ? 4 : '2_2';
    console.log("Target step determined:", targetStepNumber);

    // 5. Pre-create and activate the target step BEFORE opening popup
    console.log("About to open step:", targetStepNumber);
    await this.openStep(targetStepNumber);
    await this.waitForStepRender(targetStepNumber);
    
    // 6. Form data will be populated by loadAndPopulateFromLocalStorage() when popup opens
    console.log("Form data will auto-populate from localStorage");

    // 7. NOW open the popup - it will show the correct step immediately
    if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
      window.globalPopup.openPopup(this.element, false);
      
      // Clear restoration flag after a delay
      setTimeout(() => {
        this._isRestoring = false;
        console.log("Restoration flag cleared");
      }, 500);
      
      console.log("=== POPUP RESTORATION COMPLETE ===");

    } else {
      console.error('Global popup handler not found or openPopup method missing.');
      return;
    }
  }

  async waitForStepRender(stepNumber) {
    return new Promise(resolve => {
      const checkForForm = () => {
        const stepElement = this.element.querySelector(`.delivery-step[data-step="${stepNumber}"]`);
        
        if (!stepElement) {
          setTimeout(checkForForm, 50);
          return;
        }
        
        // Check if stepElement IS a delivery-form or contains one
        const isDeliveryForm = stepElement.tagName === 'DELIVERY-FORM';
        const hasDeliveryForm = stepElement.querySelector('delivery-form');
        
        if (isDeliveryForm || hasDeliveryForm) {
          console.log('Step render complete, delivery-form found');
          // Wait a bit more for the DeliveryForm to initialize
          setTimeout(resolve, 100);
        } else {
          setTimeout(checkForForm, 50);
        }
      };

      checkForForm();
    });
  }

  // REMOVED: restoreFormDataInStep(), verifyFormRestoration(), populateFormFieldsDirectly()
  // All replaced by loadAndPopulateFromLocalStorage() which is called automatically


  // ========================================
  // 🐛 DEBUG METHODS
  // ========================================

  debugState() {
    console.log('=== DELIVERY POPUP DEBUG ===');
    console.log('Current State:', this.getState());
    console.log('DOM Dataset:', this.element.dataset);
    console.log('Syncing state:', this._syncingState);
    console.log('==========================');
  }

  debugPricing() {
    console.log('=== PRICING DEBUG ===');
    console.log('Current state:', {
      packAll: this.state.packAll,
      quantity: this.state.quantity,
      originalQuantity: this.element.dataset.originalQuantity
    });
    console.log('Base prices:', this.state.prices);

    const finalPrices = this.calculateFinalPrices();
    console.log('Final prices:', finalPrices);

    console.log('Price breakdown:');
    console.log(`- Giftbox: ${finalPrices.baseGiftbox} × ${finalPrices.quantity} = ${finalPrices.giftbox}`);
    console.log(`- Bow: ${finalPrices.baseBow} × ${finalPrices.quantity} = ${finalPrices.bow}`);
    console.log(`- Total: ${finalPrices.total}`);
    console.log('==================');
  }

  //  NEW: Test bow quantity calculation specifically
  testBowQuantities() {
    console.log('=== BOW QUANTITY TEST ===');

    // Test packAll = false (should multiply by quantity)
    const originalPackAll = this.state.packAll;
    this.state.packAll = false;
    this.state.quantity = 3;

    let prices = this.calculateFinalPrices();
    console.log('PackAll = false, Quantity = 3:');
    console.log(`  Bow: ${prices.baseBow} × ${prices.quantity} = ${prices.bow}`);

    // Test packAll = true (should use quantity = 1)
    this.state.packAll = true;
    prices = this.calculateFinalPrices();
    console.log('PackAll = true, Quantity = 1:');
    console.log(`  Bow: ${prices.baseBow} × ${prices.quantity} = ${prices.bow}`);

    // Restore original state
    this.state.packAll = originalPackAll;
    this.state.quantity = originalPackAll ? 1 : parseInt(this.element.dataset.originalQuantity || '1');

    console.log('======================');
  }

}

customElements.define("delivery-popup", DeliveryPopup);


class GiftboxCalculator extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegateElement = new Delegate(this.element);

    // Store bound event handler for proper cleanup
    this.handleStateChange = this.handleStateChange.bind(this);
    this.popup = null;

    // Simple debouncing
    this._updateTimeout = null;

    //  DEBUG: Track calculator instances
    this._calculatorId = Math.random().toString(36).substr(2, 5);
    console.log(`Calculator instance created: ${this._calculatorId}`);
  }

  connectedCallback() {
    this.bindEvents();

    // Give popup time to initialize, then update display
    setTimeout(() => {
      this.initializeDisplay();
    }, 100);
  }

  disconnectedCallback() {
    this.delegateElement.off();

    if (this._updateTimeout) {
      clearTimeout(this._updateTimeout);
      this._updateTimeout = null;
    }

    if (this.popup) {
      this.popup.removeEventListener('delivery-popup-state-changed', this.handleStateChange);
      this.popup = null;
    }
  }

  bindEvents() {
    // Find and store popup reference
    this.popup = this.element.closest('delivery-popup');

    if (this.popup) {
      this.popup.addEventListener('delivery-popup-state-changed', this.handleStateChange);
    }

    // Handle pack all checkbox
    this.delegateElement.on('change', '#pack_all_vouchers', (e) => {
      const popup = this.element.closest('delivery-popup');
      if (popup) {
        popup.updatePackAll(e.target.checked);
      }
    });
  }

  //  ENHANCED: Better debouncing to prevent rapid multiple calls
  handleStateChange(e) {
    if (this._updateTimeout) {
      clearTimeout(this._updateTimeout);
    }

    //  Increased delay to batch multiple rapid events better
    this._updateTimeout = setTimeout(() => {
      console.log(`Calculator ${this._calculatorId} handling state change (debounced)`);
      this.updateDisplay(e.detail);
    }, 50); // Increased from 10ms to 50ms to batch better
  }

  initializeDisplay() {
    // Scan for preselected options first
    this.scanForPreselectedOptions();

    // Then update display
    this.updateDisplay();
  }

  scanForPreselectedOptions() {
    const popup = this.element.closest('delivery-popup');
    if (!popup || !popup.extractPriceFromTarget) return;

    console.log(`Calculator ${this._calculatorId} scanning for preselected options...`);

    let pricesUpdated = false;

    // Check for selected giftbox in current view
    const selectedGiftbox = this.element.querySelector('.giftbox-option[aria-selected="true"]');
    if (selectedGiftbox && selectedGiftbox.dataset.price) {
      const prices = popup.extractPriceFromTarget(selectedGiftbox, 'giftbox_product');
      if (prices && popup.state) {
        popup.state.prices.giftbox = prices.giftbox;
        popup.state.prices.bow = prices.bow;
        pricesUpdated = true;
        console.log(`Calculator ${this._calculatorId} detected preselected giftbox:`, prices);
      }
    }

    // Check for selected bow variant
    let selectedBow = this.element.querySelector('.bow-option[aria-selected="true"]');
    if (!selectedBow && popup) {
      selectedBow = popup.querySelector('.bow-option[aria-selected="true"]');
    }

    if (selectedBow && selectedBow.dataset.price) {
      const prices = popup.extractPriceFromTarget(selectedBow, 'bow_variant');
      if (prices && popup.state) {
        popup.state.prices.giftbox = prices.giftbox;
        popup.state.prices.bow = prices.bow;
        pricesUpdated = true;
        console.log(`Calculator ${this._calculatorId} detected preselected bow:`, prices);
      }
    }

    // Update datasets if prices changed
    if (pricesUpdated && popup.state) {
      popup.element.dataset.prices = JSON.stringify(popup.state.prices);

      if (popup.calculateFinalPrices) {
        const finalPrices = popup.calculateFinalPrices();
        popup.element.dataset.finalPrices = JSON.stringify(finalPrices);
        console.log(`Calculator ${this._calculatorId} updated final prices:`, finalPrices);
      }
    }
  }

  getPopupState() {
    const popup = this.element.closest('delivery-popup');
    if (!popup) return null;

    // Try to get finalPrices first (new structure)
    if (popup.dataset.finalPrices) {
      try {
        const finalPrices = JSON.parse(popup.dataset.finalPrices);
        return {
          finalPrices,
          basePrices: JSON.parse(popup.dataset.prices || '{"giftbox":0,"bow":0}'),
          packAll: popup.dataset.packAll === 'true',
          quantity: finalPrices.quantity || 1
        };
      } catch (e) {
        console.warn('Failed to parse popup finalPrices:', e);
      }
    }

    // Fallback to old structure
    return {
      basePrices: JSON.parse(popup.dataset.prices || '{"giftbox":0,"bow":0}'),
      quantity: parseInt(popup.dataset.originalQuantity || '1'),
      packAll: popup.dataset.packAll === 'true'
    };
  }

  calculateDisplayPrices(eventDetail = null) {
    // Priority 1: Event detail with finalPrices (most accurate, already multiplied)
    if (eventDetail && eventDetail.finalPrices) {
      return eventDetail.finalPrices;
    }

    // Priority 2: Get state from popup
    const state = this.getPopupState();
    if (!state) {
      return { giftbox: 0, bow: 0, total: 0 };
    }

    // Priority 3: Use finalPrices from state (already multiplied)
    if (state.finalPrices) {
      return state.finalPrices;
    }

    // Priority 4: Calculate from base prices (multiply by quantity)
    if (state.basePrices) {
      const quantity = state.packAll ? 1 : state.quantity;
      return {
        giftbox: state.basePrices.giftbox * quantity,
        bow: state.basePrices.bow * quantity,
        total: (state.basePrices.giftbox + state.basePrices.bow) * quantity,
        quantity: quantity,
        packAll: state.packAll
      };
    }

    // Fallback: zeros
    return { giftbox: 0, bow: 0, total: 0 };
  }


  updateDisplay(eventDetail = null) {
    console.log(`Calculator ${this._calculatorId} updateDisplay called with:`, eventDetail);
    const prices = this.calculateDisplayPrices(eventDetail);
    console.log(`Calculator ${this._calculatorId} calculated prices:`, prices);

    this.updateGiftboxDisplay(prices.giftbox);
    this.updateBowDisplay(prices.bow);
    this.updateTotalDisplay(prices.total);
  }

  updateGiftboxDisplay(giftboxTotal) {
    const giftboxValue = this.element.querySelector('#giftbox-value');
    if (giftboxValue) {
      giftboxValue.textContent = Shopify.formatMoney(giftboxTotal, window.theme.moneyFormat);
    }
  }

  updateBowDisplay(bowTotal) {
    const bowValue = this.element.querySelector('#bow-value');
    const bowRow = this.element.querySelector('#bow-summary-row');
    
    if (bowValue) {
      bowValue.textContent = Shopify.formatMoney(bowTotal, window.theme.moneyFormat);
    }
    
    // Hide bow row if price is 0, show if it has a price
    if (bowRow) {
      bowRow.style.display = (bowTotal === 0) ? 'none' : '';
    }
  }

  updateTotalDisplay(total) {
    const totalValue = this.element.querySelector('#delivery-total');
    if (totalValue) {
      totalValue.textContent = Shopify.formatMoney(total, window.theme.moneyFormat);
    }
  }

  debugCalculator() {
    console.log('=== CALCULATOR DEBUG ===');
    const popup = this.element.closest('delivery-popup');
    console.log('Found popup:', !!popup);
    console.log('Stored popup reference:', !!this.popup);

    if (popup) {
      console.log('Popup dataset:', popup.dataset);
    }

    const state = this.getPopupState();
    console.log('Popup state:', state);

    const prices = this.calculateDisplayPrices();
    console.log('Calculated prices:', prices);

    console.log('DOM Elements:');
    console.log('- Selected giftbox:', this.element.querySelector('.giftbox-option[aria-selected="true"]'));
    console.log('- Selected bow (in calculator):', this.element.querySelector('.bow-option[aria-selected="true"]'));

    if (popup) {
      console.log('- Selected bow (in popup):', popup.querySelector('.bow-option[aria-selected="true"]'));
    }

    console.log('- Giftbox value elem:', this.element.querySelector('#giftbox-value'));
    console.log('- Bow value elem:', this.element.querySelector('#bow-value'));
    console.log('- Total elem:', this.element.querySelector('#delivery-total'));
    console.log('========================');
  }

  forceRefresh() {
    console.log('Forcing calculator refresh...');
    this.scanForPreselectedOptions();
    this.updateDisplay();
    console.log('Calculator refresh complete');
  }
}

customElements.define("giftbox-calculator", GiftboxCalculator);